import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { LogIn } from "lucide-react";
import { supabase } from "../utils/supabaseClient";
import getSignedImageUrl from "../utils/getSignedImageUrl";
import translations from "../utils/logintranslate";

import {
  FaUserCircle,
  FaCheckCircle,
  FaExclamationCircle,
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

const image_global=async(data)=>{
   const signedUrl = await getSignedImageUrl(data.operator.picture);
            if (signedUrl) {
              //setImageUrl(signedUrl);
              localStorage.setItem(
                "cachedImage",
                JSON.stringify({
                  path: data.picture,
                  url: signedUrl,
                  expiry: Date.now() + 55 * 60 * 1000, // Expira en 55 minutos
                })
              );
            }
      }

export default function Login() {
  const URL = process.env.REACT_APP_SERVER_URL;;
  const navigate = useNavigate();

  const [language, setLanguage] = useState("es"); 
  const [showPassword, setShowPassword] = useState(false); // State for show/hide password


  const t = translations[language];

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const translateError = (errorMsg) => {
    const errorMap = {
      "User not registered": t.errors.userNotRegistered,
      "Email not verified": t.errors.emailNotVerified,
      "Inactive account": t.errors.inactiveAccount,
      "Invalid Google token": t.errors.invalidGoogleToken,
    };
    return errorMap[errorMsg] || errorMsg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${URL}/api/authOperator/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        credentials: "include", // 
      });

      const data = await response.json();

      if (response.ok) {
        setIsError(false);
        setMessage(data.message || t.loginSuccess);
        localStorage.setItem('language',data.operator.Language_operator);
        
        
        //localStorage.setItem("token", data.token);
        localStorage.setItem("id_operator", data.operator.id_operator);
        localStorage.setItem("operator", JSON.stringify(data.operator));
        localStorage.setItem("cachedImage",JSON.stringify(data.operator.picture))

        image_global(data);      
        setTimeout(() => navigate("/dashboard/service"), 1000);
      } else {
        setIsError(true);
        setMessage(translateError(data.error || t.errors.default));
      }
    } catch (error) {
      console.error("Network error:", error);
      setIsError(true);
      setMessage(t.networkError);
    }
  };

const b64urlToUtf8Json = (b64url) => {
  // Reemplaza URL-safe y agrega padding
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const json = atob(b64);
  return JSON.parse(decodeURIComponent(escape(json)));
};

const isIdTokenFresh = (idToken, skewSeconds = 60) => {
  try {
    const [, payloadB64] = idToken.split('.');
    const payload = b64urlToUtf8Json(payloadB64);
    const now = Math.floor(Date.now() / 1000);
    console.log("token data", (payload.exp - now) > -skewSeconds)
    return (payload.exp - now) > -skewSeconds;
  } catch(err) {
    console.log("error", err)
    return false;
  }
};

const handleGoogleSuccess = async (credentialResponse) => {
  const idToken = credentialResponse?.credential;
  if (!idToken) {
    setIsError(true);
    setMessage(t.googleError);
    return;
  }

  try {
    const response = await fetch(`${URL}/api/authOperator/google`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials:"include",
      body: JSON.stringify({ idToken }), 
    });

    const data = await response.json();

    if (response.ok) {
      setIsError(false);
      setMessage(data.message || t.googleLoginSuccess);

      localStorage.setItem("token", data.token);
      localStorage.setItem("id_operator", data.operator.id);
      localStorage.setItem("operator", JSON.stringify(data.operator));
      setTimeout(() => navigate("/dashboard/service"), 800);
    } else {
      if (data?.reason === "expired") {
        setMessage(t.googleError);
      } else {
        setMessage(translateError(data.error || t.googleError));
      }
      setIsError(true);
    }
  } catch (err) {
    console.error("Google login error:", err);
    setIsError(true);
    setMessage(t.networkError);
  }
};

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
        setIsError(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-[#45B69C] via-[#89B98A] to-[#E9C46A] bg-opacity-5 flex items-center justify-center p-4 relative overflow-hidden">
        
        {/* Círculos decorativos en el fondo de la página */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-20"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-25"></div>
        <div className="absolute top-1/3 right-40 w-16 h-16 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-15"></div>
        <div className="absolute bottom-1/3 left-40 w-20 h-20 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-20"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-30"></div>
        
        <div className="bg-[#FCFCF9] text-[#264653] w-full max-w-md p-8 rounded-2xl shadow-xl border border-[#DAE2E0] relative overflow-hidden">
          
          {/* Elementos decorativos orgánicos en el formulario */}
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-15"></div>
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-10"></div>
          <div className="absolute top-1/2 -right-6 w-16 h-16 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-12"></div>
          <div className="absolute bottom-8 -left-8 w-20 h-20 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-18"></div>
          <div className="absolute top-20 -left-10 w-14 h-14 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-20"></div>
          
          {/* 🌐 Select para elegir idioma */}
          <div className="flex justify-end mb-4 relative z-10">
            <label className="mr-2 text-sm text-[#264653] text-opacity-70">{t.changeLanguage}:</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-[#FCFCF9] text-[#264653] border border-[#DAE2E0] px-2 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="flex justify-center mb-4 relative z-10">
            <FaUserCircle className="text-6xl text-[#2A9D8F]" />
          </div>

          <h2 className="text-2xl font-bold mb-6 text-center text-[#264653] relative z-10">{t.title}</h2>

          {message && (
            <div
              className={`flex items-center gap-2 p-3 mb-4 rounded text-sm font-medium relative z-10 ${
                isError
                  ? "bg-red-100 text-red-800 border border-red-200"
                  : "bg-green-100 text-green-800 border border-green-200"
              }`}
            >
              {isError ? (
                <FaExclamationCircle className="text-lg" />
              ) : (
                <FaCheckCircle className="text-lg" />
              )}
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
            <div className="relative">
              <label className="block mb-1 text-[#264653] text-opacity-80">{t.email}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 pr-10 rounded-xl bg-white border border-[#DAE2E0] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] text-[#264653] placeholder-[#264653] placeholder-opacity-60 transition duration-200"
                required
              />
              <FaUser className="absolute right-3 top-11 text-[#264653] text-opacity-40" />
            </div>

            <div className="relative">
              <label className="block mb-1 text-[#264653] text-opacity-80">{t.password}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full p-3 pr-10 rounded-xl bg-white border border-[#DAE2E0] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] text-[#264653] placeholder-[#264653] placeholder-opacity-60 transition duration-200"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#264653] text-opacity-40 focus:outline-none hover:text-opacity-60 transition"
                  aria-label={showPassword ? t.hidePassword : t.showPassword}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="text-right text-sm">
              <button
                type="button"
                onClick={() => navigate("/forgotPassword")}
                className="text-[#2A9D8F] hover:text-[#21867a] underline transition font-medium"
              >
                {t.forgotPassword}
              </button>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#2A9D8F] to-[#45B69C] text-white font-semibold py-3 rounded-xl hover:from-[#21867a] hover:to-[#3aa38c] transition-all duration-200 transform hover:scale-105 shadow-md"
            >
              {t.loginButton}
            </button>
          </form>

          <div className="my-6 flex items-center relative z-10">
            <div className="flex-grow h-px bg-[#DAE2E0]" />
            <span className="px-4 text-sm text-[#264653] text-opacity-60">{t.or}</span>
            <div className="flex-grow h-px bg-[#DAE2E0]" />
          </div>

          <div className="relative z-10">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setIsError(true);
                setMessage(t.googleError);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}